import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { SecondaryButton } from '../../components/Button'
import { InlinePill } from '../../components/DashboardPrimitives'
import { TextInput } from '../../components/Input'
import { Customer, WithdrawalAccount } from '../../state/Radom'
import RecipientAvatar from './RecipientAvatar'
import { SelectedMethodIcon } from '../../components/PaymentMethodDisplay'
import { generateRoundFlagIconFromCountryCode } from '../../util/Flags'
import { getMethod } from '../../util/Managed'
import {
  RecentChip,
  RecentChipsRow,
  SectionLabel,
  SelectedRecipientCard,
  SelectionLabel,
  SelectionMeta,
  SelectionSubtext,
  focusRing
} from './SendStyles'
import {
  SendDestination,
  customerDisplayName,
  destinationDisplayName,
  destinationKey,
  destinationMode,
  detectOneOffKind,
  normalizeOneOffInput,
  oneOffKindLabel,
  withdrawalAccountSubtitle
} from './sendUtil'

const PickerWrapper = styled.div`
  position: relative;
`

const ResultsPanel = styled.div`
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e6e9ef);
  border-radius: 16px;
  /* Never taller than the space below the input: the page doesn't grow for
     absolutely-positioned content, so anything past the viewport would be
     unreachable rather than scrollable. */
  margin-top: 6px;
  /* Short enough that the actions under it stay in view on a laptop screen:
     the list scrolls, the page does not. */
  max-height: clamp(220px, calc(100vh - 470px), 340px);
  overflow-y: auto;
  position: static;
  z-index: 60;
`

const ResultsGroupLabel = styled.div`
  color: var(--text-muted, #6f7888);
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.03em;
  padding: 12px 12px 4px;
  text-transform: uppercase;
`

const ResultRow = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? 'var(--surface-hover, #f5f6f7)' : 'transparent'};
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  font-family: var(--font-sans);
  gap: 12px;
  margin: 0 4px;
  padding: 8px 8px;
  text-align: left;
  transition: background-color 0.16s ease;
  width: calc(100% - 8px);

  &:hover {
    background: var(--surface-hover, #f5f6f7);
  }

  ${focusRing}
`

const PanelFootnote = styled.div`
  border-top: 1px solid var(--surface-hover, #eef1f6);
  color: var(--text-soft, #7d8492);
  font-size: 12px;
  line-height: 1.5;
  padding: 10px 12px;
`

const PanelEmptyState = styled.div`
  color: var(--text-muted, #707887);
  font-size: 12.75px;
  line-height: 1.5;
  padding: 16px 12px;
  text-align: center;
`

const GroupFootnote = styled.div`
  color: var(--text-faint, #8a919e);
  font-size: 11.5px;
  line-height: 1.45;
  padding: 4px 12px 8px;
`

/** Type filters so a bank account is findable among dozens of wallets. */
const FilterRow = styled.div`
  background: var(--surface, #ffffff);
  border-bottom: 1px solid var(--surface-hover, #eef1f6);
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px;
  position: sticky;
  top: 0;
  z-index: 1;
`

const FilterPill = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => $active ? 'var(--ink, #20242d)' : 'var(--surface-hover, #f4f6fa)'};
  border: 0;
  border-radius: 999px;
  color: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'var(--text-muted, #5b6472)'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 7px 12px;
  transition: background-color 0.16s ease, color 0.16s ease;

  ${focusRing}
`

const destinationSubtitle = (destination: SendDestination): string => {
  switch (destination.kind) {
  case 'ownAccount':
    return withdrawalAccountSubtitle(destination.account)
  case 'customer':
    return destination.customer.email?.trim() || 'Saved recipient'
  case 'oneOff':
    return `${oneOffKindLabel(destination.oneOffKind)} · One-off destination`
  }
}

const destinationTitle = (destination: SendDestination): string => {
  switch (destination.kind) {
  case 'ownAccount':
    return destination.account.name?.trim() || 'Withdrawal account'
  case 'customer':
    return customerDisplayName(destination.customer)
  case 'oneOff':
    return destination.value
  }
}

const destinationTagLabel = (destination: SendDestination): string => {
  switch (destination.kind) {
  case 'ownAccount':
    return 'Your account'
  case 'customer':
    return 'Recipient'
  case 'oneOff':
    return oneOffKindLabel(destination.oneOffKind)
  }
}

const optionId = (index: number): string => `send-destination-option-${index}`

/** One keyboard-navigable row in the panel. */
interface PickerItem { type: 'destination', destination: SendDestination }

interface IProps {
  accounts: WithdrawalAccount[]
  customers: Customer[]
  isLoading: boolean
  canReadAccounts: boolean
  canReadCustomers: boolean
  selected?: SendDestination
  /** Recently paid destinations, rendered as one-tap chips above the search. */
  recent?: SendDestination[]
  onSelect: (destination: SendDestination) => void
  onClear: () => void
}

type RecipientFilter = 'all' | 'bank' | 'wallet' | 'contact'

const DestinationPicker = (props: IProps): ReactElement => {
  const [search, setSearch] = useState('')
  // Results live inside the card and stay visible until a destination is
  // chosen, so there is nothing to open or close.
  const isOpen = true
  const setIsOpen = (_open: boolean): void => {}
  const [filter, setFilter] = useState<RecipientFilter>('all')
  const [activeIndex, setActiveIndex] = useState(0)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const recentRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const keyboardNav = useRef(false)

  const query = search.trim().toLowerCase()

  const matchedAccounts = useMemo(() => props.accounts.filter(account => {
    if (!query) return true
    const fiat = account.accountDetails.Fiat
    const crypto = account.accountDetails.Crypto
    return [
      account.name,
      crypto?.address,
      crypto?.network,
      fiat?.accountOwnerName,
      fiat?.accountType.iban?.iban,
      fiat?.accountType.us?.accountNumber,
      fiat?.accountType.us?.bankName
    ].some(term => term?.toLowerCase().includes(query))
  }), [props.accounts, query])

  const matchedCustomers = useMemo(() => props.customers.filter(customer => {
    if (!query) return true
    return [
      customer.name,
      customer.companyName,
      customer.email,
      customer.id
    ].some(term => term?.toLowerCase().includes(query))
  }), [props.customers, query])

  const oneOffKind = detectOneOffKind(search)

  // Banks are the needle in a haystack of wallet addresses: list them first,
  // and let the filter pills cut the panel to one recipient type.
  const matchedBanks = useMemo(
    () => matchedAccounts.filter(account => !!account.accountDetails.Fiat),
    [matchedAccounts])
  const matchedWallets = useMemo(
    () => matchedAccounts.filter(account => !!account.accountDetails.Crypto),
    [matchedAccounts])

  const visibleBanks = filter === 'all' || filter === 'bank' ? matchedBanks : []
  const visibleWallets = filter === 'all' || filter === 'wallet' ? matchedWallets : []
  const visibleCustomers = filter === 'all' || filter === 'contact' ? matchedCustomers : []

  // Flat list in rendered order, so ArrowUp/Down walk exactly what's visible.
  const flatResults = useMemo((): SendDestination[] => {
    const list: SendDestination[] = []
    if (oneOffKind) {
      list.push({ kind: 'oneOff', value: normalizeOneOffInput(search), oneOffKind })
    }
    // Contacts first: in a payouts product the people you pay outrank your own
    // accounts, and the render order below must match this list.
    visibleCustomers.forEach(customer => list.push({ kind: 'customer', customer }))
    visibleBanks.forEach(account => list.push({ kind: 'ownAccount', account }))
    visibleWallets.forEach(account => list.push({ kind: 'ownAccount', account }))
    return list
  }, [oneOffKind, search, visibleBanks, visibleWallets, visibleCustomers])

  const flatItems = useMemo((): PickerItem[] =>
    flatResults.map(destination => ({ type: 'destination', destination })), [flatResults])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, isOpen, filter])

  // A closed panel forgets its filter: reopening always starts from the full
  // list, matching how search resets after a pick.
  useEffect(() => {
    if (!isOpen) setFilter('all')
  }, [isOpen])

  // Keep the keyboard-highlighted row in view; mouse hover must not scroll.
  useEffect(() => {
    if (!keyboardNav.current) return
    keyboardNav.current = false
    document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const hasSavedRecipients = props.accounts.length > 0 || props.customers.length > 0
  const hasResults = flatResults.length > 0

  const selectDestination = (destination: SendDestination): void => {
    props.onSelect(destination)
    setSearch('')
    setIsOpen(false)
  }

  const activateItem = (item: PickerItem): void => {
    selectDestination(item.destination)
  }

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!isOpen) {
        setIsOpen(true)
        return
      }
      keyboardNav.current = true
      setActiveIndex(index => e.key === 'ArrowDown'
        ? Math.min(index + 1, flatItems.length - 1)
        : Math.max(index - 1, 0))
      return
    }

    if (e.key === 'Enter' && isOpen && flatItems[activeIndex]) {
      // Selecting a result must not double as "advance the form".
      e.preventDefault()
      e.stopPropagation()
      activateItem(flatItems[activeIndex])
    }
  }

  // Own accounts are recognised by their network or bank, people by their initials.
  const renderRowIcon = (destination: SendDestination): ReactElement => {
    if (destination.kind === 'ownAccount') {
      const crypto = destination.account.accountDetails.Crypto
      if (crypto) {
        const method = getMethod(crypto.network, crypto.token)
        if (method) return <SelectedMethodIcon method={method} size={30} disableToolTip />
      } else {
        return generateRoundFlagIconFromCountryCode(destination.account.accountDetails.Fiat?.accountType.us ? 'US' : 'EU', 30)
      }
    }
    return <RecipientAvatar name={destinationDisplayName(destination)} size={32} />
  }

  const renderRow = (destination: SendDestination, index: number): ReactElement => {
    const title = destination.kind === 'oneOff'
      ? `Send to this ${destination.oneOffKind === 'iban' ? 'IBAN' : 'address'}`
      : destinationTitle(destination)
    const subtitle = destination.kind === 'oneOff'
      ? destination.value
      : destinationSubtitle(destination)

    return <ResultRow
      key={destinationKey(destination)}
      id={optionId(index)}
      role='option'
      aria-selected={index === activeIndex}
      $active={index === activeIndex}
      onMouseEnter={() => setActiveIndex(index)}
      onClick={() => selectDestination(destination)}>
      {renderRowIcon(destination)}
      <SelectionMeta>
        <SelectionLabel>{title}</SelectionLabel>
        <SelectionSubtext>{subtitle}</SelectionSubtext>
      </SelectionMeta>
      <div style={{ flexGrow: 1 }} />
      {/* Grouped rows sit under a heading that already says what they are;
          only a pasted destination needs its kind spelled out. */}
      {destination.kind === 'oneOff' && <InlinePill>{destinationTagLabel(destination)}</InlinePill>}
    </ResultRow>
  }

  if (props.selected) {
    const destination = props.selected
    return <SelectedRecipientCard>
      <div style={{ alignItems: 'center', display: 'flex', gap: 12, minWidth: 0 }}>
        <RecipientAvatar name={destinationDisplayName(destination)} size={40} />
        <SelectionMeta>
          <SelectionLabel>{destinationTitle(destination)}</SelectionLabel>
          <SelectionSubtext>{destinationSubtitle(destination)}</SelectionSubtext>
          <SelectionSubtext style={{ color: 'var(--text-faint, #8a919e)', fontSize: 11.75 }}>
            {destinationMode(destination) === 'withdrawal'
              ? 'Your own account, booked as a withdrawal'
              : 'Someone you’re paying, booked as a payout'}
          </SelectionSubtext>
        </SelectionMeta>
      </div>
      <div style={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 8 }}>
        <InlinePill>
          Books as: {destinationMode(destination) === 'withdrawal' ? 'Withdrawal' : 'Payout'}
        </InlinePill>
        <SecondaryButton
          style={{ minHeight: 34, padding: '0 12px' }}
          onClick={() => {
            props.onClear()
            setSearch('')
            setIsOpen(false)
          }}>
          Change
        </SecondaryButton>
      </div>
    </SelectedRecipientCard>
  }

  const recent = props.recent ?? []
  let rowIndex = -1

  return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {recent.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionLabel>Recent</SectionLabel>
      <RecentChipsRow ref={recentRef}>
        {recent.map(destination => {
          const name = destinationDisplayName(destination)
          return <RecentChip
            key={destinationKey(destination)}
            type='button'
            title={name}
            aria-label={`Send to ${name} again`}
            onMouseDown={e => {
              // Select on pointer-down: the input's focus/close handling runs
              // between mousedown and click and can swallow the click.
              e.preventDefault()
              selectDestination(destination)
            }}
            onClick={e => e.preventDefault()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                selectDestination(destination)
              }
            }}>
            <RecipientAvatar name={name} size={26} />
            <span>{name}</span>
          </RecentChip>
        })}
      </RecentChipsRow>
    </div>}

    <PickerWrapper ref={wrapperRef}>
      <TextInput
        ref={inputRef}
        value={search}
        autoComplete='off'
        spellCheck={false}
        role='combobox'
        aria-expanded={isOpen}
        aria-controls='send-destination-results'
        aria-activedescendant={isOpen && flatItems[activeIndex] ? optionId(activeIndex) : undefined}
        placeholder='Search saved recipients, or paste a wallet address or IBAN'
        onFocus={() => setIsOpen(true)}
        onKeyDown={onInputKeyDown}
        onChange={e => {
          setSearch(e.target.value)
          setIsOpen(true)
        }} />
      {isOpen && <ResultsPanel
        ref={panelRef}
        id='send-destination-results'
        role='listbox'
        aria-label='Send destinations'>
        {hasSavedRecipients && !props.isLoading && <FilterRow role='group' aria-label='Recipient type filters'>
          {([
            ['all', `All ${matchedBanks.length + matchedWallets.length + matchedCustomers.length}`],
            ['bank', `Bank accounts ${matchedBanks.length}`],
            ['wallet', `Wallets ${matchedWallets.length}`],
            ['contact', `Contacts ${matchedCustomers.length}`]
          ] as Array<[RecipientFilter, string]>).map(([value, label]) =>
            <FilterPill
              key={value}
              type='button'
              $active={filter === value}
              aria-pressed={filter === value}
              onMouseDown={e => e.preventDefault()}
              onClick={() => setFilter(value)}>
              {label}
            </FilterPill>)}
        </FilterRow>}

        {props.isLoading && <PanelEmptyState>Loading recipients…</PanelEmptyState>}

        {!props.isLoading && !hasResults && <PanelEmptyState>
          {query
            ? 'No recipients match. Paste a full wallet address or IBAN to send one-off.'
            : filter !== 'all'
              ? 'Nothing saved under this type yet.'
              : hasSavedRecipients
                ? 'Keep typing to filter your recipients.'
                : 'No saved recipients yet. Add one below, or paste a wallet address or IBAN here to send one-off.'}
        </PanelEmptyState>}

        {oneOffKind && <>
          <ResultsGroupLabel>One-off destination</ResultsGroupLabel>
          {renderRow(flatResults[0], (rowIndex += 1))}
        </>}

        {visibleCustomers.length > 0 && <>
          <ResultsGroupLabel>Contacts</ResultsGroupLabel>
          {visibleCustomers.map(customer => renderRow({ kind: 'customer', customer }, (rowIndex += 1)))}
          <GroupFootnote>
            Contacts can be customers, contractors, or employees: anyone you pay.
          </GroupFootnote>
        </>}
        {visibleBanks.length > 0 && <>
          <ResultsGroupLabel>Your bank accounts</ResultsGroupLabel>
          {visibleBanks.map(account => renderRow({ kind: 'ownAccount', account }, (rowIndex += 1)))}
        </>}

        {visibleWallets.length > 0 && <>
          <ResultsGroupLabel>Your wallets</ResultsGroupLabel>
          {visibleWallets.map(account => renderRow({ kind: 'ownAccount', account }, (rowIndex += 1)))}
        </>}

        {hasResults && <div style={{ height: 4 }} />}

        {(!props.canReadAccounts || !props.canReadCustomers) && <PanelFootnote>
          {!props.canReadAccounts && 'Your role can’t list saved withdrawal accounts. '}
          {!props.canReadCustomers && 'Your role can’t list recipients. '}
          Paste a full wallet address to send one-off.
        </PanelFootnote>}
      </ResultsPanel>}
    </PickerWrapper>
  </div>
}

export default DestinationPicker
